import { BookUser, Globe2, PencilLine, UserPlus, Wallet, X } from 'lucide-react';
import { useId, useState } from 'react';
import { partyApi } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queries';
import type { Draft, InvoiceKind, Party } from '../../../api/types';
import { useAccess } from '../../../app/access';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { ChoiceCards, Segmented } from '../../../ui/Choice';
import { AsyncComboBox } from '../../../ui/ComboBox';
import { Skeleton } from '../../../ui/Feedback';
import { CustomerForm, type SavedCustomer } from '../../parties/CustomerForm';
import styles from '../Sales.module.css';
import { IssueText } from './LineEditor';

type ReceiverParty = Parameters<typeof SelectedCustomer>[0]['party'];

/** How the buyer is chosen while none is: from the records, or a new one (inline form). */
export type BuyerChoice = 'records' | 'new';

/**
 * Step 1, «المشتري» (BLOCK-1-REDESIGN §A.2–A.3): where it is sold (domestic or export, with the
 * export rules' hint), then «من المشتري؟» as three choice cards: the buyer records (a server
 * search), a new buyer (an inline form, not an overlay) or an unidentified buyer (chosen at
 * once). A chosen buyer shows as a card with details, «تعديل بيانات المشتري» (the same form,
 * inline, in edit mode, also opened by a fix link: M-05) and «تغيير المشتري».
 */
export function BuyerStep({
  orgId,
  draft,
  kind,
  onKind,
  receiverParty,
  issueOf,
  onReceiver,
  choice,
  onChoice,
  isEditing,
  onEditing,
  onSaved,
}: {
  orgId: string;
  draft: Draft;
  kind: InvoiceKind;
  onKind: (kind: InvoiceKind) => void;
  receiverParty: ReceiverParty;
  issueOf: (path: string) => string | undefined;
  onReceiver: (receiver: Draft['receiver'] | undefined) => void;
  choice: BuyerChoice;
  onChoice: (choice: BuyerChoice) => void;
  /** The chosen buyer's details are open in the inline form. */
  isEditing: boolean;
  onEditing: (editing: boolean) => void;
  /** A buyer was created or corrected in the inline form. */
  onSaved: (party: SavedCustomer) => void;
}) {
  const { t } = useI18n();
  const access = useAccess(orgId);
  const questionId = useId();
  const receiverPartyId =
    draft.receiver && 'partyId' in draft.receiver ? draft.receiver.partyId : null;
  const canCreate = access.can('customer.create');
  return (
    <div className={styles.stepBody}>
      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>{t.editor.settings.type}</legend>
        <Segmented<InvoiceKind>
          aria-label={t.editor.settings.type}
          value={kind}
          onChange={onKind}
          className={styles.kindSwitch}
          options={[
            {
              value: 'i',
              label: <span data-testid="kind-local">{t.editor.settings.local}</span>,
              textLabel: t.editor.settings.local,
            },
            {
              value: 'ei',
              label: <span data-testid="kind-export">{t.editor.settings.export}</span>,
              textLabel: t.editor.settings.export,
            },
          ]}
        />
        {kind === 'ei' ? (
          <p className={styles.note}>
            <Globe2 size={12} aria-hidden="true" /> {t.editor.settings.exportHint}
          </p>
        ) : null}
      </fieldset>

      <section
        className={styles.group}
        data-testid="customer-card"
        aria-label={t.editor.sections.customer}
      >
        <div data-field="receiver" className={styles.buyerBody}>
          {!draft.receiver ? (
            <>
              <h3 id={questionId} className={styles.buyerQuestion}>
                {t.editor.buyerChoice.label}
              </h3>
              <ChoiceCards<BuyerChoice | 'cash'>
                aria-labelledby={questionId}
                value={choice}
                onChange={(next) => {
                  if (next === 'cash') onReceiver({ cash: true });
                  else onChoice(next);
                }}
                options={[
                  {
                    value: 'records',
                    title: t.editor.buyerChoice.records,
                    description: t.editor.customer.placeholder,
                    icon: <BookUser size={18} />,
                    testId: 'buyer-mode-records',
                  },
                  ...(canCreate
                    ? [
                        {
                          value: 'new' as const,
                          title: t.editor.customer.newCustomer,
                          description: t.editor.buyerChoice.newHint,
                          icon: <UserPlus size={18} />,
                          testId: 'new-customer',
                        },
                      ]
                    : []),
                  {
                    value: 'cash',
                    title: t.editor.customer.cash,
                    description: t.editor.customer.cashHint,
                    icon: <Wallet size={18} />,
                    testId: 'cash-customer',
                  },
                ]}
              />
              {choice === 'new' && canCreate ? (
                <div className={styles.inlineForm}>
                  <h3 className={styles.inlineFormTitle}>{t.parties.newTitle}</h3>
                  <CustomerForm
                    orgId={orgId}
                    partyId={null}
                    onSaved={onSaved}
                    onCancel={() => onChoice('records')}
                  />
                </div>
              ) : (
                <AsyncComboBox<Party>
                  label={t.editor.customer.search}
                  fieldName="receiver-search"
                  placeholder={t.editor.customer.placeholder}
                  value={null}
                  selectedItem={null}
                  onChange={(p) => p && onReceiver({ partyId: p.id })}
                  queryFor={(q) => ({
                    queryKey: [...queryKeys.org(orgId), 'parties', 'picker', q],
                    queryFn: async ({ signal }) =>
                      (await partyApi.list(orgId, { role: 'customer', q, limit: 20 }, signal))
                        .items,
                  })}
                  getKey={(p) => p.id}
                  getLabel={(p) => p.name}
                  renderItem={(p) => (
                    <span
                      className={styles.customerCell}
                      data-testid={`customer-option-${p.taxId ?? p.id}`}
                    >
                      <span>{p.name}</span>
                      <span className={styles.subtle}>
                        {t.parties.types[p.partyType]}
                        {p.taxId ? ` · ${p.taxId}` : ''}
                        {p.address ? ` · ${p.address.regionCity}، ${p.address.governate}` : ''}
                      </span>
                    </span>
                  )}
                  errorMessage={issueOf('receiver')}
                />
              )}
            </>
          ) : 'cash' in draft.receiver ? (
            <div className={styles.customerSelected} data-testid="selected-customer">
              <span className={styles.avatar} aria-hidden="true">
                <Wallet size={18} />
              </span>
              <span>
                <span className={styles.customerName}>{t.editor.customer.cash}</span>
                <span className={styles.customerMeta}>{t.editor.customer.cashHint}</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                icon={<X size={15} />}
                onPress={() => onReceiver(undefined)}
                testId="change-customer"
              >
                {t.editor.customer.change}
              </Button>
            </div>
          ) : isEditing && receiverPartyId ? (
            <div className={styles.inlineForm}>
              <h3 className={styles.inlineFormTitle}>{t.parties.editTitle}</h3>
              <CustomerForm
                orgId={orgId}
                partyId={receiverPartyId}
                onSaved={onSaved}
                onCancel={() => onEditing(false)}
              />
            </div>
          ) : (
            <SelectedCustomer
              orgId={orgId}
              party={receiverParty}
              canEdit={access.can('customer.edit')}
              onChange={() => onReceiver(undefined)}
              onEdit={() => onEditing(true)}
            />
          )}
          {draft.receiver && !isEditing ? <IssueText text={issueOf('receiver')} /> : null}
        </div>
      </section>
    </div>
  );
}

export function SelectedCustomer({
  party,
  canEdit,
  onChange,
  onEdit,
}: {
  orgId: string;
  party:
    | (Party & {
        addresses?: {
          country: string;
          governate: string;
          regionCity: string;
          street: string;
          buildingNumber: string;
        }[];
      })
    | null;
  canEdit: boolean;
  onChange: () => void;
  onEdit: () => void;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  if (!party) return <Skeleton height="4rem" />;
  const a = party.addresses?.[0];
  return (
    <div className={styles.customerSelected} data-testid="selected-customer">
      <span className={styles.avatar} aria-hidden="true">
        {party.name.slice(0, 1)}
      </span>
      <span>
        <span className={styles.customerName} data-testid="selected-customer-name">
          <UserText>{party.name}</UserText>
        </span>
        <span className={styles.customerMeta}>
          <span>{t.parties.types[party.partyType]}</span>
          {party.taxId ? <span className={styles.mono}>{party.taxId}</span> : null}
        </span>
        {expanded ? (
          <span
            className={styles.customerMeta}
            data-testid="customer-details"
            style={{ marginBlockStart: 'var(--space-1)' }}
          >
            {a ? (
              <span>
                {a.buildingNumber} {a.street}، {a.regionCity}، {a.governate}، {a.country}
              </span>
            ) : null}
            {party.email ? <span className={styles.mono}>{party.email}</span> : null}
            {party.phone ? <span className={styles.mono}>{party.phone}</span> : null}
          </span>
        ) : null}
        <button
          type="button"
          className={styles.sortButton}
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-link)' }}
          onClick={() => setExpanded(!expanded)}
          data-testid="customer-expand"
        >
          {expanded ? t.editor.customer.hideDetails : t.editor.customer.details}
        </button>
      </span>
      <span className={styles.customerActions}>
        {canEdit ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<PencilLine size={14} />}
            onPress={onEdit}
            testId="edit-customer"
          >
            {t.editor.customer.edit}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          icon={<X size={14} />}
          onPress={onChange}
          testId="change-customer"
        >
          {t.editor.customer.change}
        </Button>
      </span>
    </div>
  );
}
